import { PATIENT_CATEGORIES, type PatientCategory } from '../../api'
import { useT, type MsgKey } from './i18n'

/* Diabetic and Non-Diabetic on the phone screens. Same rules as the desktop
   components: two separate options, no default, one pill style everywhere. */

const KEY: Record<PatientCategory, MsgKey> = {
  diabetes: 'desk.categoryDiabetes',
  non_diabetic: 'desk.categoryNonDiabetic',
}

/** The translated name of a category; "Not set" for a patient registered before categories. */
export function useCategoryText() {
  const { t } = useT()
  return (c: PatientCategory | null | undefined) => t(c ? KEY[c] : 'desk.categoryNotSet')
}

/** Two separate choices, one button each. */
export function CategoryChoice({ id, value, onChange }: {
  id: string
  value: PatientCategory | ''
  onChange: (next: PatientCategory) => void
}) {
  const label = useCategoryText()
  return (
    <div className="m-chips" role="radiogroup" id={id} aria-labelledby={`${id}-label`}>
      {PATIENT_CATEGORIES.map((c) => (
        <button key={c} type="button" role="radio" aria-checked={value === c}
          className={`m-chip${value === c ? ' is-on' : ''}`}
          onClick={() => onChange(c)}>
          {label(c)}
        </button>
      ))}
    </div>
  )
}

export function CategoryPill({ category, compact }: { category: PatientCategory | null | undefined; compact?: boolean }) {
  const label = useCategoryText()
  const tone = category === 'diabetes' ? 'warn' : category === 'non_diabetic' ? 'ok' : 'muted'
  return (
    <span className={`m-pill ${tone}`}
      style={compact ? { padding: '1px 8px', fontSize: 'var(--m-text-xs)' } : undefined}>
      {label(category)}
    </span>
  )
}

/**
 * All · Diabetic · Non-Diabetic, with a count on each, over a day's queue.
 * The two lines are often run as separate counters, so the desk and the
 * doctor can work one at a time. A patient with no category shows under All only.
 */
export function CategoryTabs({ value, onChange, rows }: {
  value: PatientCategory | ''
  onChange: (c: PatientCategory | '') => void
  rows: { category?: PatientCategory | null }[]
}) {
  const { t } = useT()
  const text = useCategoryText()
  const count = (c: PatientCategory) => rows.filter((r) => r.category === c).length
  return (
    <div className="m-seg tight m-cat-tabs" role="tablist" aria-label={t('desk.category')}>
      <button type="button" role="tab" aria-selected={value === ''} className={value === '' ? 'is-on' : ''} onClick={() => onChange('')}>
        {t('common.all')} <span className="n">{rows.length}</span>
      </button>
      {PATIENT_CATEGORIES.map((c) => (
        <button key={c} type="button" role="tab" aria-selected={value === c} className={`${value === c ? 'is-on' : ''} cat-${c}`} onClick={() => onChange(c)}>
          <i aria-hidden="true" /> {text(c)} <span className="n">{count(c)}</span>
        </button>
      ))}
    </div>
  )
}
